import type { ErrorEvent, EventHint } from '@sentry/browser';
import { AppError, ApiError } from '@glomopay/utils';

import { stripKycTokenParam } from './strip-kyc-token-param';

const IGNORED_ERRORS: string[] = [
  'cdn.segment.com',
  'api.segment.io',
  'Error sending segment performance metrics',
  // Emscripten (dotlottie-web) console.errors these before retrying WASM with an ArrayBuffer; not a failure on its own.
  'wasm streaming compile failed',
  'falling back to ArrayBuffer instantiation',
];

const getErrorCategory = (errorCode?: string): string => {
  switch (errorCode) {
    case 'ERR_NETWORK':
      return 'network_error';
    case 'ECONNABORTED':
      return 'timeout';
    case 'ERR_CANCELED':
      return 'canceled';
    default:
      return 'unknown';
  }
};

const BREADCRUMB_URL_KEYS = ['url', 'from', 'to'];

const REFERER_HEADERS = ['Referer', 'referer'];

const stripKycTokenFromUrls = (event: ErrorEvent) => {
  const { request } = event;
  if (request?.url) request.url = stripKycTokenParam(request.url);
  if (typeof request?.query_string === 'string') request.query_string = stripKycTokenParam(request.query_string);

  const headers = request?.headers;
  REFERER_HEADERS.forEach((header) => {
    if (headers?.[header]) headers[header] = stripKycTokenParam(headers[header]);
  });

  event.breadcrumbs?.forEach(({ data }) => {
    BREADCRUMB_URL_KEYS.forEach((key) => {
      if (typeof data?.[key] === 'string') data[key] = stripKycTokenParam(data[key]);
    });
  });
};

const normalizeEndpoint = (url?: string): string => {
  if (!url) return 'unknown';
  return url.replace(/\/(payt|sub|order|ord|pay|inv|ben|cus)_[a-zA-Z0-9]+/g, '/:id');
};

export const processSentryEvent = (event: ErrorEvent, hint: EventHint): ErrorEvent | null => {
  // Drop expected errors (e.g. payment already initiated, order expired)
  if (hint.originalException instanceof AppError && hint.originalException.expected) {
    return null;
  }

  const message = event.message ?? '';
  const exceptionMessage = event.exception?.values?.[0]?.value ?? '';

  // Drop errors matching known noisy third-party patterns
  if (IGNORED_ERRORS.some((ignored) => message.includes(ignored) || exceptionMessage.includes(ignored))) {
    return null;
  }

  // Enrich ApiError events with network diagnostics
  if (hint.originalException instanceof ApiError && hint.originalException.networkDiagnostics) {
    const diag = hint.originalException.networkDiagnostics;
    const category = getErrorCategory(diag.errorCode);

    event.tags = {
      ...event.tags,
      'error.category': category,
      'network.online': diag.online !== undefined ? String(diag.online) : 'unknown',
      'api.endpoint': normalizeEndpoint(diag.requestUrl),
    };

    if (diag.connectionType) {
      event.tags['network.connectionType'] = diag.connectionType;
    }

    event.contexts = {
      ...event.contexts,
      networkDiagnostics: {
        online: diag.online,
        connectionType: diag.connectionType ?? 'unavailable',
        requestDurationMs: diag.requestDurationMs,
        errorCode: diag.errorCode,
        requestUrl: diag.requestUrl && stripKycTokenParam(diag.requestUrl),
        requestMethod: diag.requestMethod,
      },
    };

    // Fingerprint to group all network errors and timeouts into single issues
    if (category === 'network_error') {
      event.fingerprint = ['api-network-error'];
    } else if (category === 'timeout') {
      event.fingerprint = ['api-timeout-error'];
    }
  }

  stripKycTokenFromUrls(event);

  return event;
};
